/**
 * Cash With the Crews — FIELD-PAY-01 (Seth 2026-09-24: cash "should alert the
 * office that they collected cash and the installers should be routed to drop
 * it off asap"). Every cash payment taken in the field that the office hasn't
 * marked received, newest first — who has it, for which job, how much, and
 * whether the crew says it's been dropped off. Received It clears it here and
 * on the installer's Today. Renders nothing when no cash is out.
 */
import { Banknote, Loader2 } from "lucide-react";
import { useNavigateWithBack } from "@/lib/navBack";
import { useMemberName } from "@/components/TeamMemberSelect";
import { InfoTip } from "@/components/InfoTip";
import { useFieldCash, useMarkCashReceived, useFieldCashSettings } from "@/features/jobs/fieldPay";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" }) : "";

export function FieldCashPanel() {
  const navigate = useNavigateWithBack();
  const memberName = useMemberName();
  const { data: rows } = useFieldCash();
  const received = useMarkCashReceived();
  const cfg = useFieldCashSettings();
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + Number(r.amount), 0);
  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Banknote className="h-4 w-4 text-amber-600" />
        <p className="text-sm font-semibold text-slate-900">
          Cash with the crews <span className="ml-1 font-normal text-slate-500">{money(total)}</span>
        </p>
        <InfoTip label="About field cash">Cash taken on a job shows here until you mark it received. Over {money(cfg.turnInOver)}, the installer&apos;s Today tells them to bring it in the same day (Settings → Field Payments &amp; Photos).</InfoTip>
      </div>
      <ul className="divide-y divide-hairline/70">
        {rows.map((r) => (
          <li key={r.payment_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <span className="w-32 shrink-0 truncate font-medium text-slate-800">{memberName(r.collected_by_empno) ?? (r.created_by ?? "").split("@")[0] ?? "Crew"}</span>
            <button type="button" onClick={() => navigate(`/jobs/${r.job_id}`)} className="min-w-0 max-w-[260px] truncate text-left text-slate-700 hover:text-brand-700" title="Open the job">
              {r.job_title ?? "Install"}
            </button>
            <span className="text-xs text-slate-400">{when(r.collected_at)}</span>
            {r.cash_dropped_at && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Dropped off {when(r.cash_dropped_at)}</span>}
            <span className="ml-auto shrink-0 font-semibold tabular-nums text-slate-900">{money(Number(r.amount))}</span>
            <button type="button" disabled={received.isPending} onClick={() => received.mutate({ paymentId: r.payment_id })}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-brand-700 px-2.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-60">
              {received.isPending && received.variables?.paymentId === r.payment_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Received It
            </button>
          </li>
        ))}
      </ul>
      {received.isError && <p className="mt-2 text-[13px] font-medium text-rose-600">{(received.error as Error).message}</p>}
    </div>
  );
}
